import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { collection, getDocs, doc, getDoc } from "firebase/firestore";
import { db } from "../../firebase/firebase";
import {
  FaArrowLeft,
  FaShoppingCart,
  FaHeart,
  FaEllipsisH,
  FaShareAlt,
  FaBookmark,
  FaPlus,
  FaMinus,
} from "react-icons/fa";
import { addToCart, updateCartItemQuantity, getCartItemQuantity, getCartItemCount } from "../../utils/cartUtils";
import "./Products.css";
import { useLanguage } from "../../contexts/LanguageContext";

// ProductCard Component (reused from ProfileStore)
const ProductCard = ({ product, navigate }) => {
  const { translations: tRaw } = useLanguage();
  const t = { ...tRaw, productsPage: { ...tRaw?.productsPage } };
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [isLiked, setIsLiked] = useState(false);
  const [showOptions, setShowOptions] = useState(false);
  const [isHovering, setIsHovering] = useState(false);
  const [cartQuantity, setCartQuantity] = useState(0);
  const images = product.Images || [product.Image] || [];
  
  // Check if product is in cart and get quantity
  useEffect(() => {
    const quantity = getCartItemQuantity(product.id);
    setCartQuantity(quantity);
  }, [product.id]);
  
  // Auto-rotate images on hover
  useEffect(() => {
    let interval;
    if (isHovering && images.length > 1) {
      interval = setInterval(() => {
        setCurrentImageIndex((prev) => (prev + 1) % images.length);
      }, 5000); // Change every 5 seconds
    }
    return () => {
      if (interval) clearInterval(interval);
    };
  }, [isHovering, images.length]);
  
  // Close options menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showOptions && !event.target.closest('.product-options')) {
        setShowOptions(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showOptions]);
  
  const goToImage = (index) => {
    setCurrentImageIndex(index);
  };

  const toggleLike = (e) => {
    e.stopPropagation();
    setIsLiked(!isLiked);
  };

  const handleAddToCart = (e) => {
    e.stopPropagation();
    addToCart(product);
    setCartQuantity(prev => prev + 1);
  };

  const handleQuantityChange = (e, change) => {
    e.stopPropagation();
    updateCartItemQuantity(product.id, change);
    setCartQuantity(prev => {
      const newQuantity = prev + change;
      return newQuantity <= 0 ? 0 : newQuantity;
    });
  };

  const toggleOptions = (e) => {
    e.stopPropagation();
    setShowOptions(!showOptions);
  };

  const handleSave = (e) => {
    e.stopPropagation();
    setShowOptions(false);
    console.log('Save product:', product.id);
  };

  const handleShare = (e) => {
    e.stopPropagation();
    setShowOptions(false);
    console.log('Share product:', product.id);
  };

  // Calculate discount percentage
  const discountPercentage = product.SalePrice && product.Price && product.SalePrice !== product.Price
    ? Math.round(((product.Price - product.SalePrice) / product.Price) * 100)
    : 0;

  return (
    <div 
      className="product-card"
      onMouseEnter={() => setIsHovering(true)}
      onMouseLeave={() => setIsHovering(false)}
      onClick={() => navigate(`/product-details/${product.VendorId}/${product.id}`)}
    >
      <div className="product-image-container">
        <img
          src={images[currentImageIndex]}
          alt={product.Title || product.ArabicTitle}
          className="product-image"
          onError={(e) => {
            e.currentTarget.src = "/default-product.png";
          }}
        />
        
        {/* Discount Badge */}
        {discountPercentage > 0 && (
          <div className="discount-badge">
            -{discountPercentage}%
          </div>
        )}
        
        {/* Like Button */}
        <button className="product-like-btn" onClick={toggleLike}>
          <FaHeart className={isLiked ? 'liked' : ''} />
        </button>
        
        {/* Add to Cart Button / Quantity Selector */}
        {cartQuantity === 0 ? (
          <button className="product-add-btn" onClick={handleAddToCart}>
            <FaPlus />
          </button>
        ) : (
          <div className="product-quantity-selector">
            <button 
              className="quantity-btn minus" 
              onClick={(e) => handleQuantityChange(e, -1)}
            >
              <FaMinus />
            </button>
            <span className="quantity-value">{cartQuantity}</span>
            <button 
              className="quantity-btn plus" 
              onClick={(e) => handleQuantityChange(e, 1)}
            >
              <FaPlus />
            </button>
          </div>
        )}
        
        {/* Image Pagination Dots */}
        {images.length > 1 && (
          <div className="product-dots">
            {images.map((_, index) => (
              <button
                key={index}
                className={`product-dot ${index === currentImageIndex ? 'active' : ''}`}
                onClick={() => goToImage(index)}
              />
            ))}
          </div>
        )}
      </div>
      
      <div className="product-info">
        <h3 className="product-title">{product.Title || product.ArabicTitle}</h3>
        <p className="product-description">{product.Description || product.ArabicDescription}</p>
        <div className="product-price">
          {product.SalePrice && product.SalePrice !== product.Price && (
            <span className="product-original-price">${Number(product.Price ?? 0).toFixed(2)}</span>
          )}
          <span className="product-current-price">
            ${Number((product.SalePrice ?? product.Price) ?? 0).toFixed(2)}
          </span>
        </div>
        
        {/* Options Menu */}
        <div className="product-options">
          <button className="product-options-btn" onClick={toggleOptions}>
            <FaEllipsisH />
          </button>
          {showOptions && (
            <div className="product-options-menu">
              <button onClick={handleSave}>
                <FaBookmark /> {t.productsPage?.productOptions?.save || "Save"}
              </button>
              <button onClick={handleShare}>
                <FaShareAlt /> {t.productsPage?.productOptions?.share || "Share"}
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default function Products() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { translations: tRaw } = useLanguage();
  const t = { ...tRaw, productsPage: { ...tRaw?.productsPage } };

  const [userData, setUserData] = useState(null);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [cartItemCount, setCartItemCount] = useState(0);

  useEffect(() => {
    let isMounted = true;

    const fetchData = async () => {
      try {
        // Fetch user data
        const userRef = doc(db, "users", id);
        const userSnap = await getDoc(userRef);
        
        if (!userSnap.exists()) {
          navigate("/feed");
          return;
        }

        const data = userSnap.data();
        if (!isMounted) return;
        setUserData(data);

        // Fetch products from the main products collection
        const productsRef = collection(db, "products");
        const productsSnap = await getDocs(productsRef);
        
        if (!productsSnap.empty) {
          const productsData = productsSnap.docs
            .map(doc => ({
              id: doc.id,
              ...doc.data()
            }))
            .filter(product => {
              // Filter by vendor ID (store owner)
              return product.VendorId === id;
            });

          // Remove duplicate products based on product ID
          const uniqueProducts = productsData.filter((product, index, self) => 
            index === self.findIndex(p => p.id === product.id)
          );

          if (isMounted) {
            setProducts(uniqueProducts);
            // Get cart item count
            const cartCount = getCartItemCount();
            setCartItemCount(cartCount);
          }
        } else {
          if (isMounted) {
            setProducts([]);
            // Get cart item count
            const cartCount = getCartItemCount();
            setCartItemCount(cartCount);
          }
        }
      } catch (error) {
        console.error("Error fetching data:", error);
        navigate("/feed");
      } finally {
        if (isMounted) setLoading(false);
      }
    };

    fetchData();
    return () => { isMounted = false; };
  }, [id, navigate]);

  const handleBack = () => {
    navigate(-1);
  };

  const handleCart = () => {
    navigate(`/cart/${id}`);
  };

  if (loading) {
    return (
      <div className="loading-container">
        <div className="loading-animation"></div>
      </div>
    );
  }

  if (!userData) return <div className="not-found">{t.productsPage?.storeNotFound || "Store not found"}</div>;

  // Get organization data
  const orgLogo = userData.compressedOrganizationLogo || userData.organizationLogo || userData.profileImage || "/default-avatar.png";
  const orgName = userData.organizationName || userData.name || userData.username || "Store";

  return (
    <div className="products-page">
      {/* Header */}
      <div className="products-header">
        <button className="products-back-button" onClick={handleBack}>
          <FaArrowLeft />
        </button>
        
        <div className="products-center">
          <img 
            src={orgLogo} 
            alt={orgName}
            className="products-org-logo"
            onError={(e) => {
              e.currentTarget.src = "/default-avatar.png";
            }}
          />
          <h1 className="products-org-name">{orgName}</h1>
        </div>
        
        <button className="products-cart-button cart-btn" onClick={handleCart} aria-label={t.productsPage?.viewCart || "View Cart"} title={t.productsPage?.viewCart || "View Cart"}>
          <FaShoppingCart />
          {cartItemCount > 0 && (
            <span className="cart-indicator">{cartItemCount}</span>
          )}
        </button>
      </div>

      {/* Products Grid */}
      <div className="products-content">
        {products.length > 0 ? (
          <div className="products-grid">
            {products.map((product) => (
              <ProductCard key={product.id} product={product} navigate={navigate} />
            ))}
          </div>
        ) : (
          <div className="no-products">
            <div className="no-products-icon">
              <FaShoppingCart />
            </div>
            <h2>{t.productsPage?.noProductsTitle || "No Products Available"}</h2>
            <p>{t.productsPage?.noProductsSubtitle || "This store doesn't have any products yet."}</p>
          </div>
        )}
      </div>
    </div>
  );
}
